// --- src/services/geminiService.ts ---
import { InventoryMap, SupplierMap } from '../components/types';
import { logger } from './logger';

const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

// Ez a függvény végzi a "nyers" kommunikációt
const callGeminiRaw = async (promptText: string) => {
  if (!API_KEY) {
    throw new Error('Hiányzik a Gemini API kulcs!');
  }

  // A legújabb Flash modellt hívjuk közvetlenül URL-en keresztül
  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${API_KEY}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [
        {
          parts: [{ text: promptText }],
        },
      ],
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    logger.error('API Hiba részletek', errorData, 'GeminiService');
    throw new Error(
      `Google Hiba: ${response.status} - ${errorData.error?.message || 'Ismeretlen hiba'}`
    );
  }

  const data = await response.json();
  // Kinyerjük a választ a JSON-ból
  return data.candidates[0].content.parts[0].text;
};

/**
 * 1. FUNKCIÓ: Email Generálás
 */
export const generateSupplierEmail = async (
  supplierName: string,
  tone: 'formal' | 'friendly'
): Promise<string> => {
  const prompt = `
    Feladat: Írj e-mailt egy beszállítónak (${supplierName}) magyarul.
    Cél: Digitális terméklista (Excel/CSV) kérése a leltárhoz.
    Adatok: EAN, Név, Cikkszám, Ár.
    Stílus: ${tone === 'formal' ? 'Hivatalos' : 'Barátságos'}.
    Kimenet: Csak a levél szövege.
  `;

  try {
    return await callGeminiRaw(prompt);
  } catch (error: any) {
    logger.error('Email hiba', error, 'GeminiService');
    return `Hiba történt: ${error.message}`;
  }
};

/**
 * 2. FUNKCIÓ: Adatbázis Tisztítás (Chunking-alapú feldolgozás)
 * Nagy listákat (2000+ sor) 12 soros csomagokban dolgozza fel, hogy elkerülje a token limitet.
 * @param fullText - A feldolgozandó nyers szöveg
 * @param onProgress - Opcionális callback függvény a folyamatjelzéshez (százalék, aktuális CSV)
 */
export const cleanProductListWithAI = async (
  fullText: string,
  onProgress?: (percentage: number, currentFullText: string) => void
): Promise<string> => {
  const CHUNK_SIZE = 12; // Sorok száma csomagonként (csökkentve 40-ről 12-re az output token limit elkerülésére)
  const allResults: string[] = [];
  let headerLine = 'EAN,Name,SKU,Price,SellingPrice'; // Fejléc csak egyszer

  // Szöveg feldolgozása sorokra bontva
  const lines = fullText.split('\n').filter((line) => line.trim().length > 0);
  const totalLines = lines.length;

  console.log(
    `[GeminiService] Feldolgozás: ${totalLines} sor, ${Math.ceil(totalLines / CHUNK_SIZE)} csomagban`
  );

  // Chunking: 12 soros csomagokban dolgozzuk fel (csökkentve az output token limit elkerülésére)
  for (let i = 0; i < lines.length; i += CHUNK_SIZE) {
    const chunk = lines.slice(i, i + CHUNK_SIZE);
    const chunkText = chunk.join('\n');
    const chunkNumber = Math.floor(i / CHUNK_SIZE) + 1;
    const totalChunks = Math.ceil(totalLines / CHUNK_SIZE);

    try {
      console.log(
        `[GeminiService] Csomag ${chunkNumber}/${totalChunks} feldolgozása... (${chunk.length} sor)`
      );

      const prompt = `Te egy adatfeldolgozó szakértő vagy. A feladatod, hogy a következő kaotikus szövegből kinyerd a termékadatokat és ÉRVÉNYES CSV formátummá alakítsd át.

KRITIKUS SZABÁLYOK:
1. Kimenet formátum: EAN,Name,SKU,Price,SellingPrice
   - EAN: csak számok (nincs szóköz, nincs betű)
   - Name: termék neve (LÁSD ALÁBB AZ IDÉZŐJEL SZABÁLYT!)
   - SKU: cikkszám (ha nincs, generálj egyet a névből)
   - Price: nettó beszerzési ár (csak szám, tizedesjegy lehet)
   - SellingPrice: bruttó eladási ár (csak szám, tizedesjegy lehet)

2. IDÉZŐJEL SZABÁLY (KRITIKUS!):
   - TILOS idézőjelet használni a Name mezőben!
   - Példa helyes: Alma,100,SKU001,500,600
   - Példa HELYTELEN: "Alma",100,SKU003,500,600 (nincs vessző, ne használj idézőjelet!)
   - Minden más esetben TÁVOLÍTSD EL a macskakörmöket!

3. Kimenet formátum:
   - CSAK a nyers CSV adatsorokat add vissza
   - NINCS fejléc (EAN,Name,SKU,Price,SellingPrice)
   - NINCS markdown kódblokk
   - NINCS magyarázat, csak a CSV sorok

4. Ár kezelés:
   - Ha a szövegben két ár van (beszerzési és eladási), az első legyen Price, a második SellingPrice
   - Ha csak egy ár van, az legyen a Price, a SellingPrice legyen 0
   - Ha van eladási ár a bemenetben, mindenképpen add vissza mindkét árat!

5. MINDEN bemeneti sorhoz pontosan EGY kimeneti sor tartozzon!
   - NE hagyd abba a generálást a csomag vége előtt!
   - A hosszú neveket NE törd új sorba, tartsd meg egy sorban!
   - Ha nincs EAN kód, akkor is tedd be a sort, de az EAN mezőbe írd be az "N/A" értéket.

Bemenet (csomag ${chunkNumber}/${totalChunks}):
${chunkText}`;

      const chunkResult = await callGeminiRaw(prompt);

      // Tisztítás: markdown blokkok és fejlécek eltávolítása
      let cleanedChunk = chunkResult
        .replace(/```csv/gi, '')
        .replace(/```/g, '')
        .replace(/^EAN,Name,SKU,Price,SellingPrice\s*\n?/i, '') // Fejléc eltávolítása
        .trim();

      // Csak a nem üres sorokat adjuk hozzá
      const validLines = cleanedChunk.split('\n').filter((line: string) => {
        const trimmed = line.trim();
        return trimmed.length > 0 && trimmed.split(',').length >= 2;
      });

      // Adatvesztés ellenőrzés (Safety Check)
      const inputLinesCount = chunk.filter((line) => line.trim().length > 0).length;
      const outputLinesCount = validLines.length;
      if (inputLinesCount !== outputLinesCount) {
        console.warn(
          `[GeminiService] Figyelem: Adatvesztés gyanúja! Bemenet: ${inputLinesCount} sor, Kimenet: ${outputLinesCount} sor (Csomag ${chunkNumber}/${totalChunks})`
        );
      }

      if (validLines.length > 0) {
        allResults.push(...validLines);
        console.log(
          `[GeminiService] Csomag ${chunkNumber}/${totalChunks} sikeres: ${validLines.length} sor`
        );

        // Progress callback hívása: számoljuk ki a százalékot és az eddig elkészült CSV-t
        if (onProgress) {
          // A feldolgozott sorok száma: az eddig feldolgozott chunkok összege
          const processedLines = Math.min(i + CHUNK_SIZE, totalLines);
          const percentage = Math.round((processedLines / totalLines) * 100);
          // Az eddig elkészült CSV (fejléc + eddig feldolgozott sorok)
          const currentCSV = `${headerLine}\n${allResults.join('\n')}`;
          onProgress(percentage, currentCSV);
        }
      } else {
        logger.warn(
          `Csomag ${chunkNumber}/${totalChunks} üres eredmény`,
          undefined,
          'GeminiService'
        );
        // Még akkor is hívjuk a progress callbacket, ha üres az eredmény (hogy a progress bar frissüljön)
        if (onProgress) {
          const processedLines = Math.min(i + CHUNK_SIZE, totalLines);
          const percentage = Math.round((processedLines / totalLines) * 100);
          const currentCSV = `${headerLine}\n${allResults.join('\n')}`;
          onProgress(percentage, currentCSV);
        }
      }
    } catch (error: any) {
      // Egy hibás csomag ne állítsa meg az egész folyamatot
      logger.error(`Csomag ${chunkNumber}/${totalChunks} hiba`, error, 'GeminiService');
      logger.warn(
        `Csomag ${chunkNumber}/${totalChunks} kihagyva, folytatás a következővel...`,
        undefined,
        'GeminiService'
      );
      // Folytatjuk a következő csomaggal
      continue;
    }
  }

  // Összefűzés: fejléc + összes eredmény
  if (allResults.length === 0) {
    throw new Error('Nem sikerült feldolgozni egyetlen sort sem. Ellenőrizd a bemeneti adatokat!');
  }

  const finalCSV = `${headerLine}\n${allResults.join('\n')}`;
  console.log(`[GeminiService] Feldolgozás kész: ${allResults.length} sor generálva`);

  // Utolsó progress callback: 100%
  if (onProgress) {
    onProgress(100, finalCSV);
  }

  return finalCSV;
};

/**
 * 3. FUNKCIÓ: Leltár Elemzés – teljes készlet, kritikus problémák, használható javaslatok
 */
export const analyzeInventoryDiscrepancies = async (
  inventoryData: InventoryMap,
  supplierData: SupplierMap
): Promise<string> => {
  const invItems = Object.values(inventoryData);
  const invEans = new Set(Object.keys(inventoryData));
  const suppEans = Object.keys(supplierData);

  const foundCount = invEans.size;
  const missingCount = suppEans.filter((ean) => !invEans.has(ean)).length;
  const totalValue = invItems.reduce((sum, item) => {
    const price = supplierData[item.ean]?.netPrice ?? 0;
    return sum + item.quantity * price;
  }, 0);

  // Hiányzó termékek (listában nincs leltár) – max 40 név
  const missingProducts = suppEans
    .filter((ean) => !invEans.has(ean))
    .slice(0, 40)
    .map((ean) => ({ ean, name: supplierData[ean]?.name || '?' }));

  // Alacsony készlet: quantity <= min_quantity és min_quantity IS NOT NULL
  // NULL = nincs limit, 0 = érvényes limit
  const lowStock = invItems
    .filter((i) => i.min_quantity != null && i.quantity <= i.min_quantity)
    .map((i) => ({
      name: supplierData[i.ean]?.name || i.ean,
      quantity: i.quantity,
      min_quantity: i.min_quantity!,
    }))
    .slice(0, 40);

  // Nulla készlet (leltáron 0 db)
  const zeroStock = invItems.filter((i) => i.quantity === 0);
  const zeroStockNames = zeroStock.map((i) => supplierData[i.ean]?.name || i.ean).slice(0, 30);

  // Ár nélküli tételek (beszerzési ár 0 vagy hiányzik)
  const noPriceCount = invItems.filter((i) => {
    const p = supplierData[i.ean]?.netPrice ?? 0;
    return p <= 0;
  }).length;

  const summaryLines: string[] = [
    `Összesen ${invItems.length} leltározott tétel, ${suppEans.length} tétel a listában (beszállítói katalógus).`,
    `Hiányzik a leltárból: ${missingCount} tétel.`,
    `Leltározott készlet értéke (beszerzési): ${Math.round(totalValue).toLocaleString('hu-HU')} Ft.`,
    `Alacsony készlet (mennyiség ≤ minimum): ${lowStock.length} tétel.`,
    `Nulla készlet (0 db): ${zeroStock.length} tétel.`,
    `Ár nélküli (beszerzési ár hiányzik vagy 0): ${noPriceCount} tétel.`,
  ];

  if (missingProducts.length > 0) {
    summaryLines.push(
      '',
      'Hiányzó termékek (a listában van, leltáron nincs):',
      missingProducts.map((p) => `  - ${p.name} (${p.ean})`).join('\n')
    );
  }
  if (lowStock.length > 0) {
    summaryLines.push(
      '',
      'Alacsony készletű tételek (mennyiség ≤ minimum):',
      lowStock.map((p) => `  - ${p.name}: ${p.quantity} db (minimum: ${p.min_quantity})`).join('\n')
    );
  }
  if (zeroStockNames.length > 0) {
    summaryLines.push(
      '',
      'Nulla készletű tételek (0 db):',
      zeroStockNames.map((n) => `  - ${n}`).join('\n')
    );
  }

  const prompt = `Te egy raktárvezetői elemző vagy. A feladatod a teljes készlet alapján rövid, használható jelentést írni magyarul.

ADATOK – a teljes leltár összefoglalója:
${summaryLines.join('\n')}

KIMENET – kötelezően ebben a formában, magyarul:

1) KRITIKUS PROBLÉMÁK
- Sorold fel a legfontosabb problémákat (hiány, alacsony készlet, ár nélküli tételek, nulla készlet stb.), röviden és konkrétan.
- Minden problémánál mondd meg, hogy mi a konkrét kockázat (pl. kieső értékesítés, hibás árazás).

2) JAVASLATOK A JAVÍTÁSRA (most)
- Minden kritikus problémához adj 1–2 konkrét, végrehajtható lépést: mit tegyen a raktár/vezetőség rövid távon (pl. rendelés indítása, minimumok beállítása, árak pótlása).
- A javaslatok legyenek használhatóak, ne általános sablonok.

3) JAVASLATOK A JÖVŐBENI ELKERÜLÉSRE
- Folyamat- és rendszerjavaslatok: hogyan lehet a jövőben elkerülni ugyanezeket a hibákat (pl. rendszeres minimum-ellenőrzés, leltárgyakoriság, árkezelés szabályok).
- Rövid, lényegre törő, gyakorlati tippek.

Írj közvetlenül, üzleti stílusban. Ne használj bevezető vagy záró udvariasságot, csak a három blokk tartalma.`;

  try {
    return await callGeminiRaw(prompt);
  } catch (error) {
    return 'Elemzési hiba.';
  }
};
